import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { FactoryPreset, type TopologyGraphPreset } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import {
  formikSelectFieldRoundedControlStyle,
  formikSelectFieldRoundedMenuStyle,
} from 'components/FormikSelectFieldLegacy/styles';
import { GraphSidePanelNoData } from 'components/Graph/GraphSidePanel/GraphSidePanelNoData';
import { selectTopologyPresetModified } from 'components/Graph/GraphSidePanel/selectors/selectTopologyPresetModified';
import { shouldShowLeavingDialog } from 'components/Graph/commands/helpers/shouldShowLeavingDialog';
import { changeTopologyPresetDraft } from 'components/Graph/commands/settings/changeTopologyPresetDraft';
import { enterTopologyPresetEditMode } from 'components/Graph/commands/settings/enterTopologyPresetEditMode';
import { resetTopologyPreset } from 'components/Graph/commands/settings/resetTopologyPreset';
import { setAppliedPreset } from 'components/Graph/commands/settings/setAppliedPreset';
import type { ShowSidePanelCommand } from 'components/Graph/commands/showSidePanel';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { memo, useMemo } from 'react';
import type { FC } from 'react';
import Select, { type PropsValue } from 'react-select';
import { useHasPermission } from 'utils/permissions/useHasPermission';
import { isSingleOption } from 'utils/react-select/isSingleOption';
import { useGraphContextSelectors } from '../GraphContext';
import { performLeavingAction } from '../commands/leavingDialog/performLeavingAction';
import { selectGraphHasContent } from '../model/selectors/selectGraphHasContent';
import { GraphPresetControls } from './GraphPresetControls';
import { TopologyLayoutSettings } from './TopologyLayoutSettings';
import type { StpInstancesHook } from './TopologyLayoutSettingsProtocolsProtocol/useStpInstances';
import { WrenchIcon } from './icons/WrenchIcon';
import { selectAvailableEntities } from './selectors/selectAvailableEntities';
import { selectPresets } from './selectors/selectPresets';

interface OptionType {
  label: TopologyGraphPreset['name'];
  value: TopologyGraphPreset['id'];
}

const networkDataInputTab: ShowSidePanelCommand = {
  networkViewerTabId: 'dataInput',
};

interface Props {
  stpInstancesHook: StpInstancesHook;
}

const BareTopologyVisualizationSetup: FC<Props> = (props) => {
  const {
    canvasGraph: {
      graphSidePanel: { layoutSettings: layoutSettingsMsg },
    },
  } = useLocale();
  const ctxProps = useGraphContextSelectors({
    presetDraftId: prop('layoutSettings', 'presetDraft', 'id'),
    availableEntities: selectAvailableEntities,
    graphHasContent: selectGraphHasContent,
    dispatch: prop('dispatch'),
    presetEditMode: prop('layoutSettings', 'presetEditMode'),
    presetDraftState: prop('layoutSettings', 'presetDraftState'),
    presetModified: selectTopologyPresetModified,
    presets: selectPresets,
    showLeavingDialog: (s) => shouldShowLeavingDialog(s),
  });

  const hasPermission = useHasPermission();

  const handleActivePresetChange = useStableCallback(
    (option: PropsValue<OptionType>) => {
      ctxProps.dispatch(performLeavingAction, {
        callback: () => {
          if (isSingleOption(option)) {
            ctxProps.dispatch(changeTopologyPresetDraft, {
              presetId: option.value,
            });

            // Set applied preset if user changes it with empty content
            // to prevent it's resetting to factory after graph load
            if (!ctxProps.graphHasContent) {
              ctxProps.dispatch(setAppliedPreset, {
                preset:
                  ctxProps.presets.find(({ id }) => id === option.value) ??
                  FactoryPreset,
              });
            }
          }
        },
      });
    },
  );

  const editPreset = () => {
    ctxProps.dispatch(enterTopologyPresetEditMode);
    if (ctxProps.presetModified) {
      ctxProps.dispatch(resetTopologyPreset);
    }
  };

  const filterOptions: OptionType[] = useMemo(
    () =>
      ctxProps.presets.map((item) => ({
        label:
          item.id === ctxProps.presetDraftId && ctxProps.presetModified
            ? `${item.name} *`
            : item.name,
        value: item.id,
      })),
    [ctxProps.presets, ctxProps.presetModified, ctxProps.presetDraftId],
  );

  const getContextContent = () =>
    ctxProps.availableEntities.deviceTypes.length === 0 &&
    ctxProps.availableEntities.edgeSettingsIds.length === 0 ? (
      <GraphSidePanelNoData panelTabToLoadData={networkDataInputTab} />
    ) : (
      <TopologyLayoutSettings stpInstancesHook={props.stpInstancesHook} />
    );

  return (
    <div className='relative flex flex-1 flex-col'>
      <div className='flex px-5 pt-5'>
        <div className='grow'>
          <Select
            classNamePrefix='react-select'
            isClearable={false}
            isSearchable={false}
            name='graph-layout-settings-presets'
            onChange={handleActivePresetChange}
            options={filterOptions}
            styles={{
              control: (base) => ({
                ...base,
                ...formikSelectFieldRoundedControlStyle,
              }),
              menu: (base) => ({
                ...base,
                // Show menu above tabs
                zIndex: 2,
                ...formikSelectFieldRoundedMenuStyle,
              }),
            }}
            theme={(theme) => ({
              ...theme,
              borderRadius: 0,
              spacing: {
                ...theme.spacing,
                baseUnit: 2,
                controlHeight: 34,
              },
            })}
            value={filterOptions.find(
              (option) => option.value === ctxProps.presetDraftId,
            )}
          />
        </div>
        {!ctxProps.presetEditMode && hasPermission('settings') && (
          <Button
            variant='secondary'
            className='button--square ml-2.5 w-10'
            onClick={editPreset}
            type='button'
            size='base'
          >
            <WrenchIcon title='Edit preset' height={16} width={16} />
          </Button>
        )}
      </div>
      {ctxProps.presetEditMode ? (
        <>
          <TopologyLayoutSettings stpInstancesHook={props.stpInstancesHook} />
          <GraphPresetControls />
        </>
      ) : (
        getContextContent()
      )}
      {ctxProps.presetDraftState !== 'idle' && (
        <div className='match-parent background-white-semi flex flex-col justify-center'>
          <LoaderAndText
            bottomText={
              layoutSettingsMsg.presetDraftProcessing[ctxProps.presetDraftState]
            }
          />
        </div>
      )}
    </div>
  );
};

export const TopologyVisualizationSetup = memo(BareTopologyVisualizationSetup);
